import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { useLiveGames, type LiveGame } from '../api/games';
import styles from './GameList.module.css';

function matchScore(g: LiveGame, q: string): boolean {
  if (!q) return true;
  return `${g.away} ${g.home}`.toLowerCase().includes(q.toLowerCase());
}

function isLive(g: LiveGame): boolean {
  return g.detailed_state === 'In Progress';
}

export default function GameList(): JSX.Element {
  const [filter, setFilter] = useState('');
  const { data: games = [], isLoading, isError, error, refetch } = useLiveGames();
  const filtered = useMemo(() => games.filter((g) => matchScore(g, filter)), [games, filter]);

  // Search input rendered OUTSIDE the games.length guard (CLAUDE.md TS gotcha).
  return (
    <>
      <TopBar />
      <main className={styles.page} aria-labelledby="game-list-heading">
        <h1 id="game-list-heading" className={styles.heading}>Live games</h1>
        <label className={styles.search}>
          <span className={styles.searchLabel}>Filter</span>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Team or matchup"
            className={styles.searchInput}
          />
        </label>
        {isLoading && <p>Loading…</p>}
        {isError && (
          <div role="alert" className={styles.errorBox}>
            <p>Could not load live games. {(error as Error)?.message ?? ''}</p>
            <button type="button" onClick={() => refetch()}>Retry</button>
          </div>
        )}
        {!isLoading && !isError && filtered.length > 0 && (
          <ul className={styles.grid}>
            {filtered.map((g) => (
              <li key={g.game_pk}>
                <Link
                  to={`/games/${g.game_pk}`}
                  data-testid={`game-card-${g.game_pk}`}
                  className={styles.card}
                >
                  <div className={styles.cardRow}>
                    <div className={styles.teams}>
                      {g.away}
                      <br />
                      <span style={{ color: 'var(--c-ink-50)', fontWeight: 'var(--fw-regular)' }}>at </span>
                      {g.home}
                    </div>
                    <div className={styles.score}>
                      {g.away_score}–{g.home_score}
                    </div>
                  </div>
                  <div className={styles.status}>
                    <span
                      className={`${styles.statusDot} ${isLive(g) ? '' : styles.statusDotIdle}`}
                      aria-hidden="true"
                    />
                    {g.detailed_state}
                    {g.inning != null && (
                      <span className={styles.inning}>
                        {g.inning_state ?? ''} {g.inning}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {!isLoading && !isError && filtered.length === 0 && games.length === 0 && (
          <p className={styles.empty}>No games currently being played.</p>
        )}
        {!isLoading && !isError && filtered.length === 0 && games.length > 0 && (
          <p className={styles.empty}>No matches for &ldquo;{filter}&rdquo;.</p>
        )}
      </main>
    </>
  );
}
